import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { cn } from './cn.js';
const FILL_CLASSES = {
    primary: 'bg-primary',
    success: 'bg-success',
    warning: 'bg-warning',
    danger: 'bg-destructive',
    neutral: 'bg-muted-foreground',
};
// The hatch borrows the tone via text color; currentColor keeps it token-driven
// (no hardcoded hue) while the diagonal stripe reads as "pending, not done".
const HATCH_CLASSES = {
    primary: 'text-primary',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-destructive',
    neutral: 'text-muted-foreground',
};
const HATCH_STYLE = {
    backgroundImage: 'repeating-linear-gradient(45deg, currentColor 0 2px, transparent 2px 7px)',
    opacity: 0.4,
};
/**
 * Horizontal meter. A solid fill for "done" plus an optional hatched segment for
 * "pending / to review". Reports itself as a progressbar to assistive tech.
 */
export const Progress = forwardRef(({ className, value, max = 100, tone = 'primary', pending = 0, size = 'default', label, ...props }, ref) => {
    const safeMax = max > 0 ? max : 1;
    const done = Math.min(Math.max(value, 0), safeMax);
    const pend = Math.min(Math.max(pending, 0), safeMax - done);
    const donePct = (done / safeMax) * 100;
    const pendPct = (pend / safeMax) * 100;
    return (_jsxs("div", { ref: ref, role: "progressbar", "aria-valuenow": Math.round(done), "aria-valuemin": 0, "aria-valuemax": safeMax, "aria-label": label, className: cn('flex w-full overflow-hidden rounded-full bg-muted', size === 'sm' ? 'h-1.5' : 'h-2.5', className), ...props, children: [_jsx("span", { className: cn('h-full rounded-full transition-[width] duration-[var(--dur-base)] ease-[var(--ease-out)] motion-reduce:transition-none', FILL_CLASSES[tone]), style: { width: `${donePct}%` } }), pendPct > 0 && (_jsx("span", { className: cn('h-full', HATCH_CLASSES[tone]), style: { width: `${pendPct}%`, ...HATCH_STYLE }, "aria-hidden": "true" }))] }));
});
Progress.displayName = 'Progress';
